import { useCallback, useEffect, useRef, useState } from 'react';
import { message } from '@myua/kit';
import {
  AiApiKey,
  CreateAiApiKeyPayload,
  createAiApiKey,
  deleteAiApiKey,
  loadAiApiKeys,
} from './aiApi';
import { describeAiError } from './aiErrors';

// The user's registered AI provider keys (GET/POST/DELETE /ai/api-keys/).
//
// Loaded lazily while the studio modal is open — same shape as
// useMagicHistory(userId, { enabled: visible }) — because nothing outside the
// modal needs them and the list is small enough to refetch on each open rather
// than cache in redux.
//
// The list is also the source of truth for the model picker: each key carries
// the technical model names it can serve (AiApiKey.models).
//
// An empty list is *not* distinguishable via the API from "AI generation is off
// on this instance" — both answer 200 []. That ambiguity is resolved one level
// up: when generation is off, `magic_studio_enabled` in /api/asset-lab/settings
// is false and the whole feature is hidden, so inside an open modal [] can only
// mean "no keys registered yet" and we show the add form. A *failed* load (403
// without asset-lab access, 5xx) is different and surfaces `error` + a retry.

export type UseAiKeys = {
  keys: AiApiKey[];
  loading: boolean;
  // Message for a failed *load* only; add/remove failures are shown as toasts.
  error: string | null;
  reload: () => void;
  // Silent refetch, for picking up budget figures a generation just moved.
  refresh: () => void;
  addKey: (payload: CreateAiApiKeyPayload) => Promise<AiApiKey | null>;
  removeKey: (id: number) => Promise<boolean>;
};

export function useAiKeys(enabled: boolean): UseAiKeys {
  const [keys, setKeys] = useState<AiApiKey[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Bumped by reload() to re-run the load effect.
  const [reloadToken, setReloadToken] = useState(0);
  // Bumped whenever the load effect (re)starts. A silent refresh captures it and
  // only applies its answer if it's still the current session — otherwise a
  // response landing after the modal closed would resurrect the cleared list.
  const sessionRef = useRef(0);

  useEffect(() => {
    if (!enabled) {
      // Drop the previous session's list so a reopened modal never flashes stale
      // keys (e.g. after the user removed one somewhere else).
      setKeys([]);
      setError(null);
      setLoading(false);
      return;
    }
    const controller = new AbortController();
    sessionRef.current += 1;
    let alive = true;
    setLoading(true);
    setError(null);
    void loadAiApiKeys(controller.signal).then((res) => {
      if (!alive) return;
      setLoading(false);
      if (res.success) {
        setKeys(res.data.response ?? []);
        return;
      }
      // An abort resolves as a failure with status null — don't paint the panel
      // red just because the modal closed mid-flight.
      if (controller.signal.aborted) return;
      setKeys([]);
      setError(describeAiError(res.data, 'Could not load your API keys.'));
    });
    return () => {
      alive = false;
      controller.abort();
    };
  }, [enabled, reloadToken]);

  const reload = useCallback(() => setReloadToken((n) => n + 1), []);

  // Refetch the list without touching `loading` / `error`: the budget left on a
  // key drops the moment a generation is charged, and the footer shows that
  // figure, so it has to be re-read after every generation instead of only on
  // open (which is what made the number look frozen until the modal was
  // reopened). Deliberately silent — flipping `loading` would blink the Generate
  // button off, and a failed refresh must not paint an error over a generation
  // that just succeeded; the stale figure simply stays until the next attempt.
  const refresh = useCallback(() => {
    const session = sessionRef.current;
    void loadAiApiKeys().then((res) => {
      if (res.success && sessionRef.current === session) setKeys(res.data.response ?? []);
    });
  }, []);

  const addKey = useCallback(async (payload: CreateAiApiKeyPayload): Promise<AiApiKey | null> => {
    const res = await createAiApiKey(payload);
    if (!res.success) {
      // Covers 409 (duplicate name), 400 (unknown user/team) and 422 alike —
      // describeAiError already renders each of those shapes.
      void message.error(describeAiError(res.data, 'Could not save the API key.'));
      return null;
    }
    const created = res.data.response;
    // Patch the list locally: the response is the full key object, so a refetch
    // would only cost a round trip.
    setKeys((prev) => [...prev.filter((k) => k.id !== created.id), created]);
    void message.success('API key saved. It is stored encrypted on our server.');
    return created;
  }, []);

  const removeKey = useCallback(async (id: number): Promise<boolean> => {
    const res = await deleteAiApiKey(id);
    if (!res.success) {
      // Deleting a corporate / team key you don't own answers 403/404 — show the
      // backend's wording rather than pretending it worked.
      void message.error(describeAiError(res.data, 'Could not remove the API key.'));
      return false;
    }
    setKeys((prev) => prev.filter((k) => k.id !== id));
    void message.success('API key removed.');
    return true;
  }, []);

  return { keys, loading, error, reload, refresh, addKey, removeKey };
}
